import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useToastContext } from '../../../../contexts/toast'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import { DefaultConfig } from '../../../ui-config/data'
import {
  DataForConfirmationDto,
  DataForConfirmationPatchDto,
} from '../../data/model'
import { DataForConfirmationView } from './DataForConfirmation.view'

type Props = {
  data: DataForConfirmationDto
  config: DefaultConfig
  onSubmit: (data: DataForConfirmationPatchDto) => Promise<void>
  isLoading: boolean
}

interface FormValues extends DataForConfirmationDto {}
export type FormFieldName = keyof DataForConfirmationDto

export const DataForConfirmationForm: React.FC<Props> = ({
  data,
  config,
  onSubmit,
  isLoading,
}) => {
  const [isEnableErrorMsg, setIsEnableErrorMsg] = useState<boolean>(false)
  const { t } = useTranslation()

  const { showToast } = useToastContext()

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationId',
          config: config.deliveryForecast.dataForConfirmation.confirmationId,
        },
        {
          fieldName: 'department',
          config: config.deliveryForecast.dataForConfirmation.department,
        },
        {
          fieldName: 'contactName',
          config: config.deliveryForecast.dataForConfirmation.contactName,
        },
        {
          fieldName: 'contactPhone',
          config: config.deliveryForecast.dataForConfirmation.contactPhone,
        },
        {
          fieldName: 'contactMail',
          config: config.deliveryForecast.dataForConfirmation.contactMail,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: data => {
      try {
        // send data in DespatchAdviceData
        onSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'deliveryForecast.dataForConfirmation.save.messages.success.summary',
          ),
          detail: t(
            'deliveryForecast.dataForConfirmation.save.messages.success.detail',
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'deliveryForecast.dataForConfirmation.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      }
    },
  })

  const showError = (name: FormFieldName) => {
    const hasConfirmationIdError = formik.errors.confirmationId !== undefined
    const hasDepartmentError = formik.errors.department !== undefined
    const hasContactNameError = formik.errors.contactName !== undefined
    const hasContactPhoneError = formik.errors.contactPhone !== undefined
    const hasContactMailError = formik.errors.contactMail !== undefined

    return hasConfirmationIdError ||
      hasDepartmentError ||
      hasContactNameError ||
      hasContactPhoneError ||
      hasContactMailError
      ? // (hasEmailError && hasInvoiceNumberError && hasPhoneError)
        !!formik.errors[name]
      : !!(formik.touched[name] && formik.errors[name])
  }

  const isFormFieldValid = (name: FormFieldName) => showError(name)
  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }
  useEffect(() => {
    if (
      formik.errors.confirmationId !== undefined ||
      formik.errors.department !== undefined ||
      formik.errors.contactName !== undefined ||
      formik.errors.contactPhone !== undefined ||
      formik.errors.contactMail !== undefined
    ) {
      setIsEnableErrorMsg(true)
    } else {
      setIsEnableErrorMsg(false)
    }
  }, [
    isEnableErrorMsg,
    formik.errors.confirmationId,
    formik.errors.department,
    formik.errors.contactName,
    formik.errors.contactPhone,
    formik.errors.contactMail,
  ])

  const movedComponentIfErrorMsgEnable = (msgIdText: any) => {
    return isEnableErrorMsg ? (
      <div style={{ minHeight: '2.5em' }}>{getFormErrorMessage(msgIdText)}</div>
    ) : (
      <div>{getFormErrorMessage(msgIdText)}</div>
    )
  }

  return (
    <DataForConfirmationView
      data={data}
      config={config}
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      movedComponentIfErrorMsgEnable={movedComponentIfErrorMsgEnable}
      isLoading={isLoading}
    />
  )
}
